'use client';
import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { Flip } from 'gsap/all';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { MD_BREAKPOINT, SM_BREAKPOINT } from '@/lib/constants';
import { ImageSequenceScroller } from '@/components/ImageSequenceScroller';
import { useGSAP } from '@gsap/react';

const ProactiveReactiveSectionAnimation = (ref: React.RefObject<HTMLDivElement | null>) => {
  const current = ref.current;
  const video = current?.querySelector('.imageSequence');
  if (!current || !video) return;

  const container1 = current.querySelector('.textActiveSource'),
    container2 = current.querySelector('.textActiveTarget'),
    box = current.querySelector('.textActive');

  const flip = () => {
    const state = Flip.getState(box, { props: '' });
    if (!container1 || !container2 || !box) return;

    if (box.parentElement === container1) {
      container2.appendChild(box);
    } else {
      container1.appendChild(box);
    }

    Flip.from(state, {
      duration: 1,
      ease: 'expo.out',
    });
  };

  gsap.set(video, { yPercent: -50 });
  const uncover = gsap.timeline({
    paused: true,
    clearProps: 'all',
    onComplete: () => {},
  });

  uncover.to(video, { yPercent: 0, ease: 'none', duration: 1 });
  const end = video.clientHeight / 2;
  ScrollTrigger.create({
    trigger: video,
    start: 'bottom bottom',
    end: `+=${end} bottom`,
    animation: uncover,
    scrub: true,
    markers: false,
  });

  const xTarget = () => {
    if (window.innerWidth <= SM_BREAKPOINT) {
      return -10;
    } else if (window.innerWidth <= MD_BREAKPOINT && window.innerWidth > SM_BREAKPOINT) {
      return -30;
    }
    return -34;
  };
  const anim = gsap.timeline({
    defaults: {
      ease: 'none',
      duration: 1,
    },
    scrollTrigger: {
      trigger: current,
      start: 'top top',
      end: 'bottom+=200% top',
      markers: false,
      scrub: true,
      pin: true,
    },
  });

  anim
    .from(current.querySelector('.main-title'), {
      alpha: 0,
      scale: 0.75,
    })
    .add(() => {
      flip();
    })
    .add('box1')
    .from(
      current.querySelector('.box-1'),
      {
        alpha: 0,
        y: 100,
      },
      'box1',
    )
    .to(
      current.querySelector('.main-title'),
      {
        alpha: 0,
        duration: 0.5,
      },
      'box1',
    )

    .to(
      current.querySelector('.laptop'),
      {
        width: '1600px',
        xPercent: -15,
        yPercent: 25,
      },
      'box1',
    )
    .from(current.querySelector('.proactive-1'), {
      alpha: 0,
    })
    .from(
      current.querySelector('.proactive-2'),
      {
        scale: 0,
        y: -100,
      },
      '-=0.2',
    )
    .to(current.querySelector('.box-1'), {
      alpha: 1,
    })
    .add('fadeoutbox1')
    .to(
      [current.querySelector('.box-1 .pro'), current.querySelector('.box-1 .text-wrapper')],
      {
        alpha: 0,
      },
      'fadeoutbox1',
    )
    .to(
      current.querySelector('.proactive-2'),
      {
        scale: 0,
      },
      'fadeoutbox1',
    )

    .add('box2')
    .from(
      current.querySelector('.box-2'),
      {
        alpha: 0,
      },
      'box2',
    )
    .to(
      current.querySelector('.box-2'),
      {
        zIndex: 100,
      },
      'box2',
    )
    .to(
      [current.querySelector('.textActiveTarget'), current.querySelector('.ly')],
      {
        x: xTarget,
      },
      'box2+=0.5',
    )
    .to(current.querySelector('.box-2'), {
      zIndex: 100,
    })

    .from(current.querySelector('.reactive-1'), {
      alpha: 0,
    })
    .from(current.querySelector('.reactive-2'), {
      scale: 0,
      y: -200,
    });
};

const imageData = [
  {
    alt: 'Proactively 1',
    src: '/assets/images/active/SCREEN-PROACTIVE-FNL.png',
    width: 800,
    height: 500,
    className: 'image-1 absolute left-[26.2%] top-[0px] proactive-1 w-[48%] hidden lg:flex',
  },
  {
    alt: 'Proactively 2',
    src: '/assets/images/active/Proactively 02.png',
    width: 558,
    height: 357,
    className: 'absolute top-[15%] left-[22%] image-2 proactive-2 w-[48%] hidden lg:flex',
  },
  {
    alt: 'ReActively 1',
    src: '/assets/images/active/SCREEN-REACTIVE-FNL.png',
    width: 800,
    height: 500,
    className: 'image-1 absolute left-[26.2%] top-[0px] reactive-1 w-[48%] hidden lg:flex',
  },
  {
    alt: 'ReActively 2',
    src: '/assets/images/active/ReActively Section 02.png',
    width: 760,
    height: 294,
    className: 'absolute top-[15%] left-[22%] image-2 reactive-2 w-[48%] hidden lg:flex',
  },
];

const renderImages = () => {
  return imageData.map((image, index) => (
    <Image
      key={index}
      alt={image.alt}
      src={image.src}
      width={image.width}
      height={image.height}
      className={image.className}
    />
  ));
};

export const ProactiveReactiveSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger, Flip);
      ProactiveReactiveSectionAnimation(ref);
    },
    { scope: ref },
  );
  return (
    <div className="laptopSource relative hidden h-screen flex-col justify-center bg-black lg:flex" ref={ref}>
      <div className="container">
        <div className="box-wrapper relative z-10 ">
          <div className={`box-1 relative z-10 flex p-0 text-left text-white md:p-10`}>
            <div className="flex p-0">
              <div className="w-full pr-0 md:w-full md:pr-0 lg:w-1/2 lg:pr-14">
                <div className="mb-4 flex justify-center text-center text-h1-sm text-white md:text-headline-md lg:justify-normal lg:text-left lg:text-splash-md">
                  <span className="pro inline-flex w-[86px] md:w-[121px] lg:w-[147px]">Pro</span>
                  <div className="inline-flex text-gradle-blue ">
                    <p className="textActiveTarget inline-flex w-[155px] md:w-[230px] lg:w-[271px]"> </p>
                    <p className="ly inline-flex">ly</p>
                  </div>
                </div>
                <div className="text-wrapper">
                  <h2 className="text-h6 mb-1 text-center text-gray-5 lg:text-left">address avoidable failures</h2>
                  <div className="relative mb-20 mt-10 flex lg:mb-0 lg:mt-0 lg:hidden ">
                    <Image
                      src="/assets/images/active/SCREEN-PROACTIVE-FNL.png"
                      alt="reactive 1"
                      width={800}
                      height={500}
                      className="w-full "
                    />
                    <Image
                      src="/assets/images/active/Proactively 02.png"
                      alt="reactive 1"
                      width={558}
                      height={357}
                      className="image-2 absolute bottom-[-5%] right-[-10%] "
                    />
                  </div>
                  <h5 className="text-body mb-6 text-gray-4">
                    Find unreliable builds and tests, like flaky tests, learn how many people and environments are
                    affected, share information about them, and understand the root cause efficiently.
                  </h5>
                  <Link
                    href="/develocity/product/failure-analytics/"
                    className="underline-text-transition-blue group !flex !w-fit items-center gap-1"
                  >
                    <span className="text-body">Learn More</span>
                    <ChevronRightIcon className="-mb-0.5 size-6 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className={`box-2 absolute left-0 top-0 flex p-0 text-left text-white md:p-10`}>
            <div className="flex p-0">
              <div className="w-full pr-0 md:w-full md:pr-0 lg:w-1/2 lg:pr-14">
                <div className="mb-4 flex justify-center text-h1-sm text-white md:text-headline-md lg:justify-normal lg:text-splash-md">
                  <span className="pro inline-flex w-[86px] md:w-[118px] lg:w-[160px]">Re</span>
                  <div className="inline-flex text-gradle-blue ">
                    <p className="textActiveTarget inline-flex w-[166px] md:w-[300px]"> </p>
                  </div>
                </div>
                <div className="text-wrapper">
                  <h2 className="text-h6 mb-1 text-center text-gray-5 lg:text-left">troubleshoot failures</h2>
                  <div className="relative mb-20 mt-10 flex md:mb-20 md:mt-10 lg:mb-0 lg:mt-0 lg:hidden ">
                    <Image
                      src="/assets/images/active/SCREEN-REACTIVE-FNL.png"
                      alt="reactive 1"
                      width={400}
                      height={250}
                      className="w-full "
                    />
                    <Image
                      src="/assets/images/active/ReActively Section 02.png"
                      alt="reactive 1"
                      width={279}
                      height={175}
                      className="image-2 absolute bottom-[-10%] right-[-0%] "
                    />
                  </div>
                  <h5 className="text-body mb-6 text-gray-4">
                    Use Build Scan® to get detailed build action and environment data and metrics for every build to
                    rapidly determine the problem root cause. Enhance your failure analysis with Failure Analytics
                    dashboards.
                  </h5>
                  <Link
                    href="/develocity/product/build-scan/"
                    className="underline-text-transition-blue group flex w-fit items-center justify-center gap-1"
                  >
                    <span className="text-body">Learn More</span>
                    <ChevronRightIcon className="inline-block size-6 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="video laptop absolute left-1/2 top-0 w-full -translate-x-1/2 transform object-cover will-change-transform">
        <ImageSequenceScroller
          imageFolder="/assets/images/laptop-webp-seq"
          totalFrames={60}
          className="imageSequence"
          fileType="webp"
        />

        <div className="laptopTarget image-wrapper absolute left-0 top-0 h-full w-full">{renderImages()}</div>

        <div className=" main-title absolute left-1/2 top-[30vh] z-10 max-w-[300px] -translate-x-1/2 text-center md:top-[15vh] ">
          <div className="textActiveSource">
            <p className="source-flip textActive text-h1-sm text-gradle-blue md:text-headline-md lg:text-splash-md">
              Active
            </p>
          </div>
          <h5 className="text-h1-sm text-light-background-200 md:text-h4-md lg:text-h1-sm">build & test monitoring</h5>
        </div>
      </div>
    </div>
  );
};
